import React, { useEffect, useState } from "react";
import { Lock } from "lucide-react";

export function FistIcon({ size = 32, color = "#FF007F", className = "", style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className={className} style={style} aria-hidden="true">
      <g fill="#1A0833" stroke={color} strokeWidth="3.5" strokeLinejoin="round">
        <rect x="14" y="12" width="11" height="17" rx="5.5" />
        <rect x="24" y="9" width="11" height="20" rx="5.5" />
        <rect x="34" y="10" width="11" height="19" rx="5.5" />
        <rect x="44" y="14" width="9" height="16" rx="4.5" />
        <path d="M12 27 h42 v14 c0 9 -7 15 -16 15 h-8 c-9 0 -18 -6 -18 -15 z" />
        <path d="M12 33 c6 -4 16 -4 22 1 c-2 5 -8 7 -14 6" />
      </g>
    </svg>
  );
}

const fistGlow = (c) => ({ filter: `drop-shadow(0 0 6px ${c}) drop-shadow(0 0 16px ${c})` });

function useCooldown(readyAt) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, (readyAt || 0) - Date.now()));
    tick();
    if (!readyAt || readyAt <= Date.now()) return;
    const t = setInterval(tick, 200);
    return () => clearInterval(t);
  }, [readyAt]);
  return left;
}

export function SkillBar({ readyAt, cooldownS, onUse, reveal, disabled }) {
  const left = useCooldown(readyAt);
  const cooling = left > 0;
  const pct = cooling ? left / (cooldownS * 1000) : 0;
  return (
    <div data-testid="skill-bar" className={`relative flex items-center gap-2 px-2.5 py-2 rounded-2xl border-2 border-[#FF007F]/60 bg-[#0A051A]/80 backdrop-blur-md ${reveal ? "skillbar-reveal" : ""}`}
         style={{ boxShadow: "0 0 22px #FF007F55, inset 0 0 14px #9D00FF33" }}>
      <button data-testid="skill-slot-0" onClick={onUse} disabled={cooling || disabled} title={cooling ? `Ready in ${Math.ceil(left / 1000)}s` : "Neon Fists"}
              className="relative w-14 h-14 rounded-xl border-2 overflow-hidden flex items-center justify-center transition-transform active:scale-90 hover:scale-105 disabled:hover:scale-100"
              style={{ borderColor: cooling ? "#5B3BFF" : "#FF007F", background: "radial-gradient(circle, #FF007F33, #1A0833)", boxShadow: cooling ? "none" : "0 0 16px #FF007F" }}>
        <FistIcon size={40} color={cooling ? "#7D6BB3" : "#FF3399"} style={cooling ? undefined : fistGlow("#FF007F")} className={cooling ? "" : "skill-ready-bob"} />
        {cooling && (
          <>
            <div className="absolute inset-0" style={{ background: `conic-gradient(rgba(10,5,26,0.8) ${pct * 360}deg, transparent 0deg)` }} />
            <span data-testid="skill-cooldown" className="absolute font-num text-xl text-white" style={{ textShadow: "0 0 6px #000" }}>{Math.ceil(left / 1000)}</span>
          </>
        )}
      </button>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} data-testid={`skill-slot-${i}`} title="Locked — unlocks later"
             className="w-12 h-12 rounded-xl border-2 border-dashed border-[#5B3BFF]/60 bg-[#1A0833]/70 flex flex-col items-center justify-center text-[#7D6BB3]">
          <Lock size={16} />
          <span className="font-mono text-[8px] tracking-widest mt-0.5">SOON</span>
        </div>
      ))}
      <style>{`
        .skillbar-reveal { animation: skillbar-in 0.7s cubic-bezier(.2,1.6,.4,1) both; }
        @keyframes skillbar-in { 0% { opacity: 0; transform: translateY(30px) scale(0.6); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .skill-ready-bob { animation: skill-bob 1.2s ease-in-out infinite; }
        @keyframes skill-bob { 50% { transform: scale(1.08) rotate(-6deg); } }
      `}</style>
    </div>
  );
}

// Two neon fists spawn by the wrestler, aim, then rocket into the enemy portrait.
export function FistStrike({ fx }) {
  if (!fx) return null;
  return (
    <div key={fx.key} className="pointer-events-none absolute inset-0 z-40" data-testid="fist-strike">
      {[0, 1].map((i) => (
        <React.Fragment key={i}>
          <div className="fist-fly absolute" style={{ "--sy": `${38 + i * 16}%`, "--ty": `${46 + i * 10}%`, animationDelay: `${i * 160}ms` }}>
            <FistIcon size={110} color="#FF3399" style={fistGlow("#FF007F")} />
          </div>
          <div className="fist-impact absolute rounded-full" style={{ top: `${46 + i * 10}%`, animationDelay: `${520 + i * 160}ms` }} />
        </React.Fragment>
      ))}
      <style>{`
        .fist-fly { left: 24%; top: var(--sy); opacity: 0; transform: translate(-50%, -50%) scale(0.2) rotate(-40deg); animation: fist-fly 0.72s cubic-bezier(.5,0,.75,0) forwards; }
        @keyframes fist-fly {
          0% { left: 24%; top: var(--sy); opacity: 0; transform: translate(-50%,-50%) scale(0.2) rotate(-40deg); }
          18% { left: 24%; top: var(--sy); opacity: 1; transform: translate(-50%,-50%) scale(1.15) rotate(0deg); }
          40% { left: 20%; top: var(--sy); opacity: 1; transform: translate(-50%,-50%) scale(1) rotate(0deg); }
          74% { left: 76%; top: var(--ty); opacity: 1; transform: translate(-50%,-50%) scale(1.25) rotate(4deg); }
          86% { left: 76%; top: var(--ty); opacity: 1; transform: translate(-50%,-50%) scale(1.55) rotate(-4deg); }
          100% { left: 76%; top: var(--ty); opacity: 0; transform: translate(-50%,-50%) scale(1.9); }
        }
        .fist-impact { left: 76%; width: 40px; height: 40px; margin: -20px 0 0 -20px; opacity: 0; border: 6px solid #FFE600;
          box-shadow: 0 0 30px #FFE600, 0 0 60px #FF007F, inset 0 0 20px #FF007F; animation: fist-impact 0.45s ease-out forwards; }
        @keyframes fist-impact { 0% { opacity: 1; transform: scale(0.3); } 100% { opacity: 0; transform: scale(5); } }
      `}</style>
    </div>
  );
}

export function LevelUpBurst({ fx }) {
  if (!fx) return null;
  return (
    <div key={fx.key} className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center" data-testid="level-up-burst">
      <div className="lvl-rays absolute w-[140%] aspect-square" />
      <div className="lvl-text text-center">
        <div className="font-graffiti text-6xl sm:text-7xl leading-none" style={{ color: "#FFE600", textShadow: "0 0 18px #FFE600, 0 0 40px #FF007F, 4px 4px 0 #000" }}>LEVEL UP!</div>
        <div className="font-num text-3xl mt-1 text-[#00F0FF]" style={{ textShadow: "0 0 12px #00F0FF, 2px 2px 0 #000" }}>LVL {fx.level}</div>
      </div>
      <style>{`
        .lvl-rays { background: repeating-conic-gradient(#FFE60055 0 8deg, transparent 8deg 22deg); border-radius: 50%;
          -webkit-mask: radial-gradient(circle, #000 20%, transparent 65%); mask: radial-gradient(circle, #000 20%, transparent 65%);
          animation: lvl-rays 1.8s ease-out forwards; }
        @keyframes lvl-rays { 0% { opacity: 0; transform: scale(0.3) rotate(0deg); } 20% { opacity: 1; } 100% { opacity: 0; transform: scale(1.1) rotate(90deg); } }
        .lvl-text { animation: lvl-text 1.8s cubic-bezier(.2,1.5,.4,1) forwards; }
        @keyframes lvl-text { 0% { opacity: 0; transform: scale(2.4); } 15% { opacity: 1; transform: scale(0.95); } 25% { transform: scale(1.05); } 80% { opacity: 1; transform: scale(1); } 100% { opacity: 0; transform: translateY(-30px) scale(0.9); } }
      `}</style>
    </div>
  );
}
